import classNames from 'classnames';
import * as R from 'ramda';
import React from 'react';
import { connect } from 'react-redux';
import { GROUP_BY_QUERY_TYPE } from '../../../support/analyticsConstants';

import AppsStore from '../../../../main/stores/AppsStore_LEGACY';
import { getDruidFiltersByDimension as getDruidFiltersByDimensionAction } from '../../../actions/DruidActions';
import { alterQuery } from '../../../support/utils';
import {
  convertMultipleQueriesToNativeDruidFilters,
  convertNativeFiltersToDisplayFormat,
  getListOfDimensionsAndValues,
  mergeFilters,
} from '../../../support/utils/druid/nativeQueries';
import { updateFilter } from '../../../support/utils/druid/updateQuery';
import { getServerSideTopicNamesForTrends } from '../../../support/utils/eo_helpers';
import DruidQuery from '../../core/DruidQuery/DruidQuery';
import PureQueryBuilder from '../PureQueryBuilder';
import MultiEventSelect from '../PureQueryBuilder/MultiEventSelect';
import FraudSelect from './FraudSelect';
import {
  addStandardSourceQueryForEvents,
  deriveOrganic,
  dimensionsAreNotAvailable,
  generateAdditionalQueriesFromEvents,
  getDefaultGranularityFromContext,
  getEventForTimeseries,
  getNonDimensionColumnKeysFromSelectedColumns,
  removeEventDescriptionsFromEvents,
  updateDimensionsWithDate,
} from './utils';

import store from 'client/js/redux';

import { DomainAnalyticsService } from 'client/js/main/services/analytics/DomainAnalyticsService';
import type { RootState } from 'client/js/redux';
import { NativeQueriesService } from '../../../../../../shared/NativeQueries.service';
import { SummaryProductContextService } from '../../../../../../shared/SummaryProductContext.service';
import { ANALYTICS_CONTEXT } from '../../../../../../shared/constants/analytics';
import type { ReportActiveMode } from '../../../../../../shared/types/roi-hub/TableModeSwitcher.types';

import { getColumnDataFieldKey } from '../../../../../../shared/utils/queryBuilder';
import { DruidQueryHashService } from '../../../../common/services/druid/DruidQueryHashService';
import { getSampleEndDate, shouldLoadPartialData } from '../../../../common/support/analytics/longRangePartialData';
import { SK_ADNETWORK_DATASOURCE } from '../../../../common/support/eo_mapping/datasources';
import { DEFAULT_BLOCKED_EVENT_INDICATOR } from '../../../../main/screens/ScreenAdsFraud/fraudConstants';
import { getState as selectAccessControl } from '../../../../main/selectors/AccessControlSelectors';
import { isTouchPointAnalyticsAndDashboardReportingEnabled } from '../../../../main/selectors/ProductSelectors';
import type { Query } from '../../../../main/types/druidTypes';
import { getState as selectAdPartner } from '../../../../redux/domains/AdPartner/AdPartner.selectors';
import { getAdPartners as getAdPartnersAction } from '../../../../redux/domains/AdPartner/actions';
import { setPageState as setPageStateAction } from '../../../actions/ShareableActions';
import { getDruidState } from '../../../selectors/DruidSelectors';
import { selectNamespaceOrderBy } from '../../../selectors/ShareableSelectors';
import { selectDisableOrganicToggle } from './selectors';

type Props = {
  /**
   * maximum number of dimension fields allowed in query
   */
  maxDimensions: number;
  /**
   * minimum number of dimension fields allowed in query
   */
  minDimensions: number;
  /**
   * query start date
   */
  start_date: string;
  /**
   * query end date
   */
  end_date: string;
  /**
   * array of sources to query. Ex: ['eo_click', 'eo_install']
   * see common/support/eo_mapping/datasources.js for more sources
   */
  data_sources: string[];
  /**
   * default filter for initial query
   */
  defaultFilter: any;
  /**
   * default dimensions for initial query
   */
  default_dimensions: string[];
  /**
   * filters that are required and can't be changed.
   * will overwrite defaultFilter if there is overlap
   */
  requiredFilter: any;
  /**
   * required filters that we do not wish to expose to the user.
   * introduced with the new summary page in April 2020.
   * filter valued passed in will be merged with the user-created filter before the DruidQuery is requested
   * not passed in to PureQueryBuilder since we do not want the user to see this filter
   */
  implicitRequiredFilter: any;
  /**
   * filter that will override even the filters stored in
   * localstorage
   */
  overrideInitialFilter: any;
  /**
   * dimensions that will override dimensions stores in localstorage
   */
  overrideInitialDimensions: string[];
  /**
   * source specific options for the query
   * example:
   *  {
   *    eo_install: {
   *      dimensions: [ 'hours_from_last_attributed_touch_to_event' ] }
   *    }
   * }
   */
  dataSourceSpecificQueryOptions: any;
  /**
   * use dataSourceSpecificQueryOptions as the DruidQuery sourceQuery if true.
   * used for the new summary page since we want the sourceQuery to be based on the selected product
   * instead of being read from shareable state
   */
  useDataSourceSpecificQueryOptionsAsSourceQuery: boolean;
  /**
   * do not add custom events to the event dropdown if true
   */
  excludeCustomEvents: boolean;
  testid: string;
  /**
   * context passed as a property of DruidQuery.query -- TODO MAKE REQUIRED
   */
  context: string;
  eo_cutoff_date: any;
  /**
   * overrides for v1/druid.js logic. see DruidQuery.jsx for shape
   */
  overrides: any;
  /**
   * Initial state for whether we are requesting unique data or not. Note
   * 'unique' state is set from this prop but then maintained as state on this
   * component. Warning: DruidQuery.jsx will append aggregations and set metric
   */
  initialUnique: boolean;
  /**
   * Though we always want to show the 'Unique' checkbox, we sometimes want
   * it disabled (like on Summary time series page) where user can't toggle
   */
  disableUniqueToggle: boolean;
  /**
   * Initial state for whether we are requesting organic data or not. Note
   * 'organic' state is set from this prop but then maintained as state on this
   * component. Warning: DruidQuery.jsx will append aggregations and set metric
   */
  initialOrganic: boolean;
  /**
   * Though we always want to show the 'Organic' checkbox, we sometimes want
   * it disabled (like on Summary time series page) where user can't toggle
   */
  disableOrganicToggle: boolean;
  /**
   * Determines the initial state of the 'Web Only' filter.
   * If true, the query will initially be restricted to web-only data.
   */
  initialWebOnly: boolean;
  /**
   * If true, disables the 'Web Only' toggle in the UI.
   * Prevents users from manually toggling the web-only filter.
   */
  disableWebOnlyToggle: boolean;
  /**
   * If this DruidQueryBuilder will have an export button (which will still
   * need to be passed in as a child), we need to pass bool in as prop because
   * the 'Unique' checkbox will need to be moved over to make room
   */
  hasExportButton: boolean;
  /**
   * If this DruidQueryBuilder will have a web only toggle.
   */
  hasWebOnlyToggle: boolean;
  /**
   * If this DruidQueryBuilder will have a unique count toggle.
   */
  hasUniqueToggle: boolean;
  /**
   * If this DruidQueryBuilder will have a organic count toggle.
   */
  hasOrganicToggle: boolean;
  /**
   * If this DruidQueryBuilder will have a Split SKAN data toggle.
   */
  hasSplitSkanDataToggle: boolean;
  /**
   * List of event names to choose from for timeseries charts.
   * If empty, event name dropdown will not appear
   */
  eventList: string[];
  /**
   * (optional) Overrides the default druid filters list
   */
  filterTypesList: string[];
  /**
   * Refer to DLA-2398
   *
   * (optional) Overrides the default druid filter options on filter rows (not compare by row).
   * Note, this prop is necessary because there are cases when the filter options in the compare by row need to different from the filter options on the filter rows.
   */
  filterTypesListFilterRow: string[];
  /**
   * shows a title with gradient at the top of the analytics component
   */
  title: string;
  /**
   * (optional) Overrides event labels (descriptions) that are displayed i.e. in Filters section dropdowns
   */
  eventLabelsOverride: any;
  /**
   * List of ads fraud indicators for dropdown. Used to select preset column displays for different types of fraud.
   */
  fraudIndicators: any;
  defaultFraudIndicator: string;
  minEvents: number;
  maxEvents: number;
  debounceQuery: boolean;
  partialLoadOnLongRange: boolean;

  // Used to identify changes to selected columns/datasources and reset runFullReport flag
  columnKeys: string[];

  // Used to signify that the underlying query for tabular data will be executed using the domain analytics paradigm
  useAnalyticsReport: boolean;

  // Used to signify that the underlying query for time-series data will be executed using the domain analytics paradigm
  useAnalyticsTrends: boolean;

  /**
   * Default: false. If set to true and the query.dimensions in the shareable state are not matching the
   * available dimensions from 'filterTypesList' -> the query.dimensions are reset to 'default_dimensions'.
   */
  resetDimensionsIfNotAvailable: boolean;

  /**
   * Default: true. If set to true and the 'default_dimensions' change -> the query.dimensions are reset to
   * 'default_dimensions'.
   */
  resetDimensionsOnDefaultDimensionsChange: boolean;

  /**
   * Default: false. If set to true, the dimensions explanation (if returned from API) will be passed down to the table
   * More information about explanation data in the cenarius docs: https://www.notion.so/brancheng/Cenarius-GeneralV2-Endpoint-ed7e3f80ee584bebaad207ae45e4fdea?pvs=4
   */
  useExplanation: boolean;
  draggableCompareBy: boolean;
  /**
   * Optional. Default: flat. If set to tree, when using tree view mode on report table
   */
  reportActiveMode: string;
  /**
   * Optional. Default: undefined. Represents currency code picked in currency switcher if available
   */
  toCurrency: string;

  shareableNamespace: string;
  setPageState: (namespace, state) => void;
  getAdPartners: () => void;
  getDruidFiltersByDimension: (dateRange, dimensions, dataSources) => void;
  defaultBlockedEventIndicator: string;
  toggleFraudDescription: (showing: boolean) => void;
  granularity?: string;
  type?: string;
  druid: any;
  accessControl: any;
  adPartner: any;
  orderBy?: { field: string; direction: string };
  isTouchPointAnalyticsEnabled?: boolean;
  children: any;
  blockedEventIndicators: any;
  hasFraudDescriptionToggle?: boolean;
  showFraudDescription?: boolean;
  customLabelMap?: Record<string, any>;
  hideCompareBy?: boolean;
  lightweightFiltersEnabled?: boolean;
  hideTableModeSwitcher?: boolean;
};

class DruidQueryBuilder extends React.Component<Props> {
  static defaultProps = {
    maxDimensions: 2,
    minDimensions: 0,
    maxEvents: 2,
    minEvents: 1,
    data_sources: ['eo_click'],
    defaultFilter: {},
    default_dimensions: [],
    initialUnique: true,
    initialOrganic: false,
    initialWebOnly: false,
    disableUniqueToggle: false,
    disableOrganicToggle: false,
    disableWebOnlyToggle: false,
    hasExportButton: false,
    hasUniqueToggle: true,
    hasOrganicToggle: true,
    hasWebOnlyToggle: true,
    testid: 'analytics',
    debounceQuery: false,
    partialLoadOnLongRange: false,
    useAnalyticsReport: false,
    useAnalyticsTrends: false,
    resetDimensionsIfNotAvailable: false,
    resetDimensionsOnDefaultDimensionsChange: true,
    useExplanation: false,
    draggableCompareBy: false,
    reportActiveMode: 'flat',
  };

  queryHashService: DruidQueryHashService = new DruidQueryHashService();

  domainAnalytics = DomainAnalyticsService.build();

  _getShareableStateForNamespace() {
    const { shareableNamespace } = this.props;
    return store.getState().shareable.getPageState(shareableNamespace || 'other');
  }

  _getShareableState() {
    const { context } = this.props;
    return this._getShareableStateForNamespace()[context] || {};
  }

  _setShareableState(data) {
    if (typeof data.runFullReport !== 'boolean') {
      data.runFullReport = false;
    }
    const { context, setPageState, shareableNamespace } = this.props;
    setPageState(shareableNamespace, {
      [context]: R.merge(this._getShareableState(), data),
    });
  }

  componentWillMount() {
    const {
      getAdPartners,
      data_sources,
      eventList,
      defaultFilter,
      requiredFilter,
      overrideInitialFilter,
      overrideInitialDimensions,
      default_dimensions,
      initialOrganic,
      initialUnique,
      initialWebOnly,
      defaultBlockedEventIndicator,
      dataSourceSpecificQueryOptions,
      context,
      toggleFraudDescription,
      disableOrganicToggle,
      hasOrganicToggle,
      isTouchPointAnalyticsEnabled,
    } = this.props;

    let shareableState = this._getShareableState();
    if (shareableState.sourceQuery) {
      shareableState = {
        ...shareableState,
        sourceQuery: convertMultipleQueriesToNativeDruidFilters(shareableState.sourceQuery),
      };
    }

    getAdPartners();

    const filter = mergeFilters(defaultFilter || {}, requiredFilter || {});

    const events = getEventForTimeseries(data_sources, eventList, isTouchPointAnalyticsEnabled);

    const sourceQuery = addStandardSourceQueryForEvents(dataSourceSpecificQueryOptions, events);

    const organic = deriveOrganic({
      shareableStateOrganic: shareableState.organic,
      disableOrganicToggle,
      initialOrganic,
      hasOrganicToggle,
    });

    shareableState = {
      ...shareableState,
      organic,
    };

    // [EG-3024] Set granularity and dimensions for shareable state stored before GroupBy Interval was supported
    const shareableDimensions = overrideInitialDimensions || (shareableState.query && shareableState.query.dimensions);
    const defaultGranularity = getDefaultGranularityFromContext(context);
    const updatedDimensions = updateDimensionsWithDate(
      shareableState.granularity || defaultGranularity,
      shareableDimensions || default_dimensions
    );
    if (updatedDimensions.includes('fraud_codes')) {
      toggleFraudDescription(true);
    }

    const initState = {
      /**
       * event represents the event selected in the SHOW: dropdown for timeseries chart
       */
      events: events,
      query: {
        data_sources: data_sources,
        filter,
        dimensions: updatedDimensions,
      },
      sourceQuery,
      unique: initialUnique,
      organic,
      webOnly: initialWebOnly,
      granularity: defaultGranularity,
      blockedEventIndicator: defaultBlockedEventIndicator,
    };

    if (shareableState.sourceQuery && !Object.keys(shareableState.sourceQuery).length) {
      shareableState = {
        ...shareableState,
        sourceQuery,
      };
    }

    this._setShareableState(R.merge(initState, shareableState));
    const overrideState = {
      dimensions: updatedDimensions,
    };
    if (overrideInitialFilter) {
      Object.assign(overrideState, {
        filter: mergeFilters(overrideInitialFilter, requiredFilter || {}),
        filters: null,
      });
      this.updateQuery(overrideState);
      this.updateFraudType(null);
      this.updateBlockedEventIndicator('none');
    }
  }

  _getQuery = () => {
    const props = this.props;
    let shareableQuery = new NativeQueriesService().convertFiltersToNativeDruidFilters(
      this._getShareableState().query || {}
    );
    const shareableState = this._getShareableState();

    // required filters should always be used
    const filter = mergeFilters(shareableQuery.filter || {}, props.requiredFilter || {});

    shareableQuery = Object.assign(shareableQuery, {
      filter,
      filters: undefined,
    });

    const newQuery = Object.assign(
      {
        app_id: AppsStore.getAppId(),
        start_date: props.start_date,
        end_date: props.end_date,
        granularity: props.granularity || 'all',
        eo_cutoff_date: props.eo_cutoff_date,
        context: props.context,
      },
      shareableQuery
    ) as Query;

    return alterQuery(newQuery, shareableState.unique, shareableState.webOnly);
  };

  /**
   * Get deduplicated non null/empty list of data sources from the
   * selected columns
   * Currently, this only works for tables.
   * @return {Array<String>}
   */
  _getDataSources = () => {
    const { context } = this.props;
    const shareableState = this._getShareableStateForNamespace();
    let selectedColumns = shareableState.selectedColumns;

    if (SummaryProductContextService.isSummaryProductContext(context)) {
      selectedColumns = shareableState[context]?.selectedColumns || [];
      return [
        ...new Set(
          selectedColumns &&
            selectedColumns.flatMap((column) => {
              return column.dataSources;
            })
        ),
      ]
        .filter((column) => !!column)
        .sort();
    }

    switch (context) {
      case 'skadnetwork-table':
        selectedColumns = shareableState[context].selectedColumns;
        if (!selectedColumns) {
          return [];
        }

        if (selectedColumns.find((column) => column.key === SK_ADNETWORK_DATASOURCE)) {
          return [SK_ADNETWORK_DATASOURCE];
        }

        return [];

      case 'skadnetwork-time-series':
        return removeEventDescriptionsFromEvents(shareableState[context].events || []);
      case 'fraud-table':
      case ANALYTICS_CONTEXT.JOURNEYS_HISTORY_TABLE:
        selectedColumns = shareableState[context].selectedColumns;
        return [
          ...new Set(
            selectedColumns &&
              selectedColumns.flatMap((column) => {
                return (
                  column.dataSources &&
                  column.dataSources.flatMap((source) => {
                    return source.includes('_blocked') ? [source, source.split('_blocked')[0]] : source;
                  })
                );
              })
          ),
        ]
          .filter((column) => !!column)
          .sort();
      case ANALYTICS_CONTEXT.SUMMARY_TABLE:
      case ANALYTICS_CONTEXT.SOURCES_TABLE:
      case ANALYTICS_CONTEXT.EMAILS_TABLE:
      case ANALYTICS_CONTEXT.JOURNEYS_TABLE:
      case ANALYTICS_CONTEXT.ADS_TABLE:
      case ANALYTICS_CONTEXT.ROI_ANALYTICS_TABLE:
      case ANALYTICS_CONTEXT.SEO_TABLE:
      case ANALYTICS_CONTEXT.QR_CODE_LINK_TABLE:
      case ANALYTICS_CONTEXT.SKAN_UNIFIED_TABLE:
      case ANALYTICS_CONTEXT.OTT_TABLE:
        return [
          ...new Set(
            selectedColumns &&
              selectedColumns.flatMap((column) => {
                return column.dataSources;
              })
          ),
        ]
          .filter((column) => !!column)
          .sort();
      default:
        if (shareableState[context].query.data_sources) return shareableState[context].query.data_sources.sort();
    }
  };

  _getColumns = () => {
    const { context } = this.props;
    const shareableState = this._getShareableStateForNamespace();
    let columns: any[] = [];

    if (SummaryProductContextService.isSummaryProductContext(context)) {
      columns = shareableState[context]?.selectedColumns || [];
    }

    switch (context) {
      case 'skadnetwork-table':
      case 'fraud-table':
      case ANALYTICS_CONTEXT.JOURNEYS_HISTORY_TABLE:
        columns = shareableState[context].selectedColumns || [];
        break;
      case ANALYTICS_CONTEXT.SUMMARY_TABLE:
      case ANALYTICS_CONTEXT.SOURCES_TABLE:
      case ANALYTICS_CONTEXT.EMAILS_TABLE:
      case ANALYTICS_CONTEXT.JOURNEYS_TABLE:
      case ANALYTICS_CONTEXT.ADS_TABLE:
      case ANALYTICS_CONTEXT.ROI_ANALYTICS_TABLE:
      case ANALYTICS_CONTEXT.SEO_TABLE:
      case ANALYTICS_CONTEXT.QR_CODE_LINK_TABLE:
      case ANALYTICS_CONTEXT.TRUEVIEW_TABLE:
      case ANALYTICS_CONTEXT.SKAN_UNIFIED_TABLE:
      case ANALYTICS_CONTEXT.OTT_TABLE:
        columns = shareableState.selectedColumns || [];
        break;
    }

    return columns
      .map((c) => getColumnDataFieldKey(c.key))
      .filter((reportColumnKey) => reportColumnKey)
      .sort();
  };

  _getTopics = () => {
    const shareableState = this._getShareableStateForNamespace();
    const topics = shareableState?.[this.props.context]?.events || [];
    const serverSideTopicNames = topics.map(getServerSideTopicNamesForTrends);
    return serverSideTopicNames;
  };

  _getOrderBy = () => {
    const shareableState = this._getShareableStateForNamespace();
    if (!shareableState.localSortDisabled) {
      return;
    }

    return this.props.orderBy;
  };

  _redactQueryBasedOnAccess = (query) => {
    const props = this.props;

    const filterDimensionsAndValues = getListOfDimensionsAndValues(query.filter);

    const partnerReportFilters =
      filterDimensionsAndValues && filterDimensionsAndValues.last_attributed_touch_data_tilde_advertising_partner_name;
    if (
      partnerReportFilters &&
      props.adPartner.hasPartnersLoaded &&
      props.accessControl.data.ad_networks.access_filter instanceof Array
    ) {
      const partnersUserCanAccess = {};
      const availablePartners = props.adPartner.partners;
      props.accessControl.data.ad_networks.access_filter.forEach((machineName) => {
        const adPartner = availablePartners.find((partner) => partner.machine_name === machineName);

        if (adPartner) {
          // check company name(for regular ad partners) or publisher_name(for custom ad partners)
          partnersUserCanAccess[adPartner.company_name || adPartner.publisher_name] = adPartner;
        }
      });

      const redactedPartnerQuery = partnerReportFilters.filter((partnerName) => partnersUserCanAccess[partnerName]);
      if (partnerReportFilters.length !== redactedPartnerQuery.length) {
        const row = convertNativeFiltersToDisplayFormat(query.filter).filter(
          (v) => v.dimension === 'last_attributed_touch_data_tilde_advertising_partner_name'
        )[0];
        const filter = updateFilter(query.filter, row, null, null, redactedPartnerQuery);
        return {
          ...query,
          filter,
        };
      }
    }
    return query;
  };

  componentWillReceiveProps(nextProps) {
    const { data_sources, default_dimensions, eventList, filterTypesList, context, start_date, end_date, columnKeys } =
      nextProps;
    const shareableState = this._getShareableState();

    if (this.props.start_date !== start_date || this.props.end_date !== end_date) {
      this._setShareableState({ runFullReport: false });
    }

    if (columnKeys && this.props.columnKeys && !R.equals(columnKeys, this.props.columnKeys)) {
      this._setShareableState({ runFullReport: false });
    }

    const shouldResetDimensionsIfNotAvailable =
      this.props.resetDimensionsIfNotAvailable &&
      dimensionsAreNotAvailable(filterTypesList, this._getShareableState().query.dimensions);

    const shouldResetDimensionsOnDefaultDimensionsChange =
      this.props.resetDimensionsOnDefaultDimensionsChange && !R.equals(default_dimensions, this.props.default_dimensions);

    const shouldResetDimensions = shouldResetDimensionsIfNotAvailable || shouldResetDimensionsOnDefaultDimensionsChange;

    if (shouldResetDimensions) {
      this._setShareableState({
        query: {
          ...shareableState.query,
          dimensions: default_dimensions,
        },
      });
    }

    // reset this.state.query from props if data_sources has changed
    if (!R.equals(data_sources, this.props.data_sources)) {
      this.changeEvent(getEventForTimeseries(data_sources, eventList, this.props.isTouchPointAnalyticsEnabled));
    }

    // [EG-3024] Set granularity and dimensions for bookmarks created before GroupBy Interval was supported
    const shareableDimensions = shareableState.query && shareableState.query.dimensions;
    const defaultGranularity = getDefaultGranularityFromContext(context);
    const updatedDimensions = updateDimensionsWithDate(
      shareableState.granularity || defaultGranularity,
      shareableDimensions || this.props.default_dimensions
    );

    if (!shareableState.granularity) {
      this._setShareableState({ granularity: defaultGranularity });
    }

    if (!R.equals(updatedDimensions, shareableDimensions)) {
      this.updateQuery({ dimensions: updatedDimensions });
    }

    const derivedOrganic = deriveOrganic({
      shareableStateOrganic: shareableState.organic,
      disableOrganicToggle: nextProps.disableOrganicToggle,
      initialOrganic: nextProps.initialOrganic,
      hasOrganicToggle: nextProps.hasOrganicToggle,
    });
    if (shareableState.organic !== derivedOrganic) {
      this._setShareableState({ organic: derivedOrganic });
    }
  }

  changeEvent = (events) => {
    const { dataSourceSpecificQueryOptions } = this.props;
    const sourceQuery = addStandardSourceQueryForEvents(dataSourceSpecificQueryOptions, events);

    this._setShareableState({
      events,
      sourceQuery,
    });
  };

  updateWebOnly = (event) => {
    this._setShareableState({ webOnly: event.target.checked });
  };

  updateUnique = (event) => {
    this._setShareableState({ unique: event.target.checked });
  };

  updateOrganic = (event) => {
    this._setShareableState({ organic: event.target.checked });
  };

  updateSkanSplit = (event) => {
    const { context, setPageState, shareableNamespace } = this.props;

    setPageState(shareableNamespace, {
      [context]: this._getShareableState(),
      splitSkanData: event.target.checked,
    });
  };

  updateBlockedEventIndicator = (blockedEventIndicator) => {
    const shareableState = this._getShareableState();
    // only set blockedEventIndicator if shareable state already has a value set for blockedEventIndicator
    if (!!shareableState.blockedEventIndicator) {
      this._setShareableState({ blockedEventIndicator });
    }
  };

  updateBlockedEventType = (config) => {
    if (!config) {
      this._setShareableState({
        blockedEventIndicator: DEFAULT_BLOCKED_EVENT_INDICATOR,
      });
      return;
    }

    const shareableState = this._getShareableState();
    this._setShareableState({
      blockedEventIndicator: config.value || DEFAULT_BLOCKED_EVENT_INDICATOR,
      fraudIndicator: 'custom',
      selectedColumns: config.selectedColumns || shareableState.selectedColumns,
      activeHeader: config.activeHeader || shareableState.activeHeader,
      isReversedHeader: false,
      granularity: 'all',
    });
  };

  updateFraudType = (config) => {
    if (!config) {
      this._setShareableState({ fraudIndicator: 'custom' });
      return;
    }

    const shareableState = this._getShareableState();
    this._setShareableState({
      blockedEventIndicator: 'none',
      fraudIndicator: config.value || 'custom',
      selectedColumns: config.selectedColumns || shareableState.selectedColumns,
      activeHeader: config.activeHeader || shareableState.activeHeader,
      isReversedHeader: false,
      granularity: 'all',
    });
  };

  updateFraudTypeOnCompareByChange = () => {
    this._setShareableState({
      fraudIndicator: 'custom',
    });
  };

  updateQuery = (nextQuery) => {
    const shareableState = this._getShareableState();
    this._setShareableState({
      query: R.merge(shareableState.query, nextQuery),
    });
  };

  updateActiveMode = (activeMode: ReportActiveMode) => {
    if (!this.domainAnalytics.isDrillDownEnabled(this.props.context)) {
      return;
    }
    this._setShareableState({ activeMode });
  };

  updateGranularity = (config) => {
    const shareableState = this._getShareableState();

    // do not update shareableState or query if any of the following are falsey
    if (!config || !shareableState || !shareableState.query || !shareableState.query.dimensions) {
      return;
    }

    const defaultGranularity = getDefaultGranularityFromContext(this.props.context);
    const updatedGranularity = config.value || defaultGranularity;
    const updatedDimensions = updateDimensionsWithDate(updatedGranularity, shareableState.query.dimensions);

    this._setShareableState({ granularity: updatedGranularity });

    this.updateQuery({
      granularity: updatedGranularity,
      dimensions: updatedDimensions,
    });
  };

  fetchFilters = (dimension) => {
    const shareableState = this._getShareableState();
    const { start_date, end_date } = this.props;
    const { query } = shareableState;
    return this.props.getDruidFiltersByDimension({ start_date, end_date }, [dimension], query.data_sources);
  };

  /**
   * Updates showFraudDescription state in parent. Additionally, updates
   * the dimensions with 'fraud_codes' if showFraudDescription is toggled to
   * be 'true'
   */
  _toggleFraudDescription = (e: any) => {
    const { toggleFraudDescription } = this.props;
    let dimensions = this._getShareableState().query.dimensions;
    if (e.target.checked) {
      if (!dimensions.includes('fraud_codes')) {
        dimensions = [...dimensions, 'fraud_codes'];
        this.updateQuery({ dimensions });
      }
      return toggleFraudDescription(true);
    }

    return toggleFraudDescription(false);
  };

  _generateAdditionalQueries = () => {
    const { context } = this.props;
    const shareableState = this._getShareableState();
    const query = this._getQuery();
    switch (context) {
      case 'skadnetwork-table':
        return generateAdditionalQueriesFromEvents({
          query,
          events: getNonDimensionColumnKeysFromSelectedColumns(shareableState.selectedColumns),
        });
      case 'skadnetwork-time-series':
        return generateAdditionalQueriesFromEvents({
          query,
          events: shareableState.events || [],
        });
      default:
        return undefined;
    }
  };

  _removeDateRangeFromDruidQueryDimensions = (dimensions: string[]) => {
    return dimensions?.filter((dimension) => dimension !== 'date_range');
  };

  getEventsList = () => {
    const { eventList, druid, excludeCustomEvents } = this.props;

    const completeEventList = eventList && eventList.concat(druid.custom_event_list);

    return excludeCustomEvents ? eventList : completeEventList;
  };

  render() {
    const shareableState = this._getShareableState();
    const namespaceState = this._getShareableStateForNamespace();
    const query = Object.assign({}, this._getQuery(), {
      data_sources: this._getDataSources(),
    });

    const {
      druid,
      maxDimensions,
      minDimensions,
      maxEvents,
      minEvents,
      requiredFilter,
      implicitRequiredFilter,
      title,
      disableUniqueToggle,
      hasExportButton,
      testid,
      hasUniqueToggle,
      hasOrganicToggle,
      hasWebOnlyToggle,
      hasSplitSkanDataToggle,
      blockedEventIndicators,
      defaultBlockedEventIndicator,
      fraudIndicators,
      defaultFraudIndicator,
      context,
      hasFraudDescriptionToggle,
      dataSourceSpecificQueryOptions,
      useDataSourceSpecificQueryOptionsAsSourceQuery,
      showFraudDescription,
      customLabelMap,
      disableOrganicToggle,
      disableWebOnlyToggle,
      debounceQuery,
      partialLoadOnLongRange,
      useAnalyticsReport,
      useAnalyticsTrends,
      eventLabelsOverride,
      shareableNamespace,
      useExplanation,
      draggableCompareBy,
      reportActiveMode,
      toCurrency,
      hideCompareBy,
      lightweightFiltersEnabled,
    } = this.props;

    const isPartialLoad = partialLoadOnLongRange && !shareableState.runFullReport && shouldLoadPartialData(query);

    if (isPartialLoad) {
      query.end_date = getSampleEndDate(query);
    }

    const dimensionsToCompareBy = this.props.filterTypesList;
    const dimensionsToFilterBy = this.props.filterTypesListFilterRow || dimensionsToCompareBy;

    const redactedQuery = this._redactQueryBasedOnAccess(query);

    const sourceQuery = useDataSourceSpecificQueryOptionsAsSourceQuery
      ? addStandardSourceQueryForEvents(dataSourceSpecificQueryOptions, shareableState.events)
      : shareableState.sourceQuery;

    const { filter: userFilter } = query;
    const mergedFilter = mergeFilters(userFilter, implicitRequiredFilter, true);
    const druidQuery = Object.assign({}, query, { filter: mergedFilter });
    const additionalQueries = this._generateAdditionalQueries();
    const isDomainAnalyticsContext = this.domainAnalytics.isDomainAnalyticsAvailable(druidQuery);

    let reportQuery;
    let trendsQuery;
    if (isDomainAnalyticsContext) {
      if (useAnalyticsReport) {
        reportQuery = {
          start_date: druidQuery.start_date,
          end_date: druidQuery.end_date,
          granularity: druidQuery.granularity,
          context: druidQuery.context,
          dimensions: this._removeDateRangeFromDruidQueryDimensions(druidQuery.dimensions),
          columns: this._getColumns(),
          orderBy: this._getOrderBy(),
          toCurrency,
        };
      }

      if (useAnalyticsTrends) {
        trendsQuery = {
          start_date: druidQuery.start_date,
          end_date: druidQuery.end_date,
          granularity: druidQuery.granularity,
          context: druidQuery.context,
          compareBy: druidQuery.dimensions?.find((dimension) => dimension !== 'date_range'),
          topics: this._getTopics(),
        };
      }
    }

    const hash = this.queryHashService.getHash({
      query: druidQuery,
      sourceQuery: sourceQuery,
      reportQuery: !trendsQuery ? reportQuery : undefined,
      trendsQuery: !reportQuery ? trendsQuery : undefined,
      type: this.props.type || GROUP_BY_QUERY_TYPE,
      overrides: this.props.overrides,
      reportActiveMode,
    });

    const queryResults = druid.queries[hash];
    const explanation = queryResults?.explanation;

    return (
      <div className="analytics" data-testid={this.props.testid}>
        {title ? <div className="analytics__title">{title}</div> : null}
        <div className={classNames('analytics__container', { 'analytics__container-with-title': !!title })}>
          <PureQueryBuilder
            eventSelect={
              <MultiEventSelect
                events={shareableState.events}
                eventList={this.getEventsList()}
                updateEvent={this.changeEvent}
                queryParam={'data_sources'}
                maxEvents={maxEvents}
                minEvents={minEvents}
                eventLabelsOverride={eventLabelsOverride}
              />
            }
            fraudSelect={
              <FraudSelect
                blockedEventIndicator={shareableState.blockedEventIndicator}
                blockedEventIndicators={blockedEventIndicators}
                defaultBlockedEventIndicator={defaultBlockedEventIndicator}
                updateBlockedEventType={this.updateBlockedEventType}
                fraudIndicator={shareableState.fraudIndicator}
                fraudIndicators={fraudIndicators}
                defaultFraudIndicator={defaultFraudIndicator}
                updateFraudType={this.updateFraudType}
                updateOrganic={this.updateOrganic}
              />
            }
            dimensions={query.dimensions}
            updateFraudTypeOnCompareByChange={this.updateFraudTypeOnCompareByChange}
            dimensionsToCompareBy={dimensionsToCompareBy}
            maxDimensions={maxDimensions}
            minDimensions={minDimensions}
            allFilters={druid.filters}
            filter={redactedQuery.filter}
            fetchFilters={this.fetchFilters}
            dimensionsToFilterBy={dimensionsToFilterBy}
            requiredFilter={requiredFilter}
            unique={shareableState.unique}
            updateUnique={this.updateUnique}
            disableUniqueToggle={disableUniqueToggle}
            organic={shareableState.organic}
            updateOrganic={this.updateOrganic}
            disableOrganicToggle={disableOrganicToggle}
            webOnly={shareableState.webOnly}
            updateWebOnly={this.updateWebOnly}
            disableWebOnlyToggle={disableWebOnlyToggle}
            hasFraudDescriptionToggle={hasFraudDescriptionToggle}
            showFraudDescription={showFraudDescription}
            toggleFraudDescription={this._toggleFraudDescription}
            hasExportButton={hasExportButton}
            testid={testid}
            hasUniqueToggle={hasUniqueToggle}
            hasOrganicToggle={hasOrganicToggle}
            hasWebOnlyToggle={hasWebOnlyToggle}
            hasSplitSkanDataToggle={hasSplitSkanDataToggle}
            splitSkanData={namespaceState.splitSkanData}
            updateSkanSplit={this.updateSkanSplit}
            updateQuery={this.updateQuery}
            updateGranularity={this.updateGranularity}
            granularity={shareableState.granularity}
            updateBlockedEventIndicator={this.updateBlockedEventIndicator}
            context={context}
            query={druidQuery}
            customLabelMap={customLabelMap}
            namespace={shareableNamespace}
            explanation={useExplanation && explanation ? explanation : undefined}
            draggableCompareBy={draggableCompareBy}
            hideCompareBy={hideCompareBy}
            lightweightFiltersEnabled={lightweightFiltersEnabled}
            activeMode={reportActiveMode}
            updateActiveMode={this.updateActiveMode}
            hideTableModeSwitcher={this.props.hideTableModeSwitcher}
          />
          <DruidQuery
            type={this.props.type || GROUP_BY_QUERY_TYPE}
            query={druidQuery}
            sourceQuery={sourceQuery}
            overrides={this.props.overrides}
            unique={shareableState.unique}
            organic={shareableState.organic}
            webOnly={shareableState.webOnly}
            additionalQueries={additionalQueries}
            isPartialLoad={isPartialLoad}
            debounce={debounceQuery}
            reportQuery={!trendsQuery ? reportQuery : undefined}
            trendsQuery={!reportQuery ? trendsQuery : undefined}
            reportActiveMode={reportActiveMode}
          >
            {React.Children.map(this.props.children, (child) => {
              if (child) {
                return (
                  React.cloneElement(child, {
                    events: shareableState.events,
                    data_sources: this.props.data_sources,
                  }) || null
                );
              }
            })}
          </DruidQuery>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState, ownProps) => ({
  druid: getDruidState(state),
  accessControl: selectAccessControl(state),
  adPartner: selectAdPartner(state),
  disableOrganicToggle: selectDisableOrganicToggle(state, ownProps),
  orderBy: selectNamespaceOrderBy(state, ownProps),
  isTouchPointAnalyticsEnabled: isTouchPointAnalyticsAndDashboardReportingEnabled(state),
});

const mapDispatchProps = (dispatch) => ({
  setPageState: (namespace, state) => dispatch(setPageStateAction(namespace, state)),
  getAdPartners: () => dispatch(getAdPartnersAction({})),
  getDruidFiltersByDimension: (dateRange, dimensions, dataSources) =>
    dispatch(getDruidFiltersByDimensionAction(dateRange, dimensions, dataSources)),
});

export default connect(mapStateToProps, mapDispatchProps)(DruidQueryBuilder);
